import { Button } from '@components/Button/Button';
import { FieldTitle } from '@components/FieldTitle/FieldTitle';
import { Input } from '@components/Input/Input';
import { Select } from '@components/Select/Select';
import { FieldError } from '@screens/ReportCreation/components/fields/FieldError';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import type { ControllerRenderProps, FieldPath } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { ScrollView, XStack, YStack } from 'tamagui';
import type {
  UserFormConfig,
  UserFormField,
  UserFormValues,
} from './userForm.types';
import { getTextValue } from './userForm.utils';

interface UserFormProps {
  config: UserFormConfig;
  defaultValues: UserFormValues;
  isOpen: boolean;
  onSubmit: (values: UserFormValues) => void;
  onCancel: () => void;
}

export const UserForm = ({
  config,
  defaultValues,
  isOpen,
  onSubmit,
  onCancel,
}: UserFormProps): ReactNode => {
  const formMethods = useForm<UserFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitted },
  } = formMethods;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    reset(defaultValues);
  }, [defaultValues, isOpen, reset]);

  const renderFieldInput = (
    field: UserFormField,
    controllerField: ControllerRenderProps<
      UserFormValues,
      FieldPath<UserFormValues>
    >,
  ): React.ReactNode => {
    if (field.type === 'text') {
      return (
        <Input
          value={getTextValue(controllerField)}
          onChangeText={controllerField.onChange}
          placeholder={field.placeholder}
          keyboardType={field.keyboardType}
          autoCapitalize={field.autoCapitalize}
        />
      );
    }

    if (field.type === 'select') {
      return (
        <Select
          options={field.options}
          value={getTextValue(controllerField)}
          onValueChange={controllerField.onChange}
          placeholder={field.placeholder}
        />
      );
    }

    return (
      <Input value={field.displayValue} editable={false} disabled />
    );
  };

  return (
    <YStack>
      <ScrollView>
        <YStack gap="$4" marginTop="$4">
          {config.fields.map((field) => (
            <Controller
              key={field.id}
              control={control}
              name={field.id}
              rules={field.rules}
              render={({ field: controllerField, fieldState }) => (
                <YStack gap="$2">
                  <FieldTitle
                    label={field.label}
                    required={Boolean(field.rules?.required)}
                  />
                  {renderFieldInput(field, controllerField)}
                  <FieldError
                    message={
                      isSubmitted ? fieldState.error?.message : undefined
                    }
                  />
                </YStack>
              )}
            />
          ))}
        </YStack>
      </ScrollView>

      <XStack marginTop="$4" justifyContent="flex-end" gap="$2">
        <Button
          variant="outlined"
          title={config.cancelLabel}
          onPress={onCancel}
        />
        <Button title={config.submitLabel} onPress={handleSubmit(onSubmit)} />
      </XStack>
    </YStack>
  );
};
